import { Injectable } from '@angular/core';
import { ContractDetailsAPI } from '../interface/arrangment-cron-api.interface';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class ContractStatusService {

  constructor() { }

  /**
   * Calculates the contract status data based on the provided ContractDetailsAPI response.
   * Logic:
   * - has_active_arrangement = FALSE -> "Completed"
   * - has_active_arrangement = TRUE && active_arrangement.days_until < 0 -> "Overdue"
   * - has_active_arrangement = TRUE (and days_until >= 0) -> "Active"
   * 
   * @param data The ContractDetailsAPI response containing the contracts.
   * @returns An array of objects suitable for a pie chart, containing the counts for each status.
   */
  public calculateContractStatus(data: ContractDetailsAPI | null | undefined): any[] {
    let completed = 0;
    let active = 0;
    let overdue = 0;

    if (data && data.contracts && Array.isArray(data.contracts)) {
      for (const contract of data.contracts) {
        if (!contract.has_active_arrangement) {
          completed++;
        } else if (contract.has_active_arrangement && contract.active_arrangement && contract.active_arrangement.days_until < 0) {
          overdue++;
        } else if (contract.has_active_arrangement) {
          active++;
        }
      }
    }

    return [
      { label: 'Completed', value: completed, color: '#4f46e5' },
      { label: 'Active',    value: active,    color: '#10b981' },
      { label: 'Overdue',   value: overdue,   color: '#f59e0b' }
    ];
  }

  /**
   * Calculates the execution gap timing based on arrangement counts rather than amount.
   * Logic matches TrendsComponent but aggregates count = 1 per entry instead of amount.
   * 
   * @param data The ContractDetailsAPI response
   * @returns Array with timing details
   */
  public calculateExecutionGap(data: ContractDetailsAPI | null | undefined): any[] {
    let sameDay = 0;
    let oneToSeven = 0;
    let eightToFourteen = 0;
    let fifteenToThirty = 0;
    let thirtyOnePlus = 0;

    if (data && data.contracts && Array.isArray(data.contracts)) {
      data.contracts.forEach((contract) => {
        const entries = [...(contract.entry_details || [])];
        if (entries.length === 0) return;

        // Sort by arrangement date ascending to determine attempts sequence
        entries.sort((a, b) => dayjs(a.arrangement_date).unix() - dayjs(b.arrangement_date).unix());
        
        entries.forEach((entry) => {
          const creationDate = dayjs(entry.creation_date);
          const arrangementDate = dayjs(entry.arrangement_date);
          const daysToExecution = arrangementDate.diff(creationDate, 'day');

          // Increment count instead of amount
          const count = 1;

          if (daysToExecution <= 0) sameDay += count;
          else if (daysToExecution <= 7) oneToSeven += count;
          else if (daysToExecution <= 14) eightToFourteen += count;
          else if (daysToExecution <= 30) fifteenToThirty += count;
          else thirtyOnePlus += count;
        });
      });
    }

    return [
      { label: 'Same Day', value: sameDay, color: '#10b981' },
      { label: '1-7 Days', value: oneToSeven, color: '#ef4444' },
      { label: '8-14 Days', value: eightToFourteen, color: '#fbbf24' },
      { label: '15-30 Days', value: fifteenToThirty, color: '#3b82f6' },
      { label: '31+ Days', value: thirtyOnePlus, color: '#a855f7' }
    ];
  }

  /**
   * Calculates the timeline execution distribution based on arrangement counts.
   * Specific to the Arrangement Execution Timeline chart buckets.
   * 
   * @param data The ContractDetailsAPI response
   * @returns Array with timeline distribution metrics mapped to 0-30, 31-60, etc.
   */
  public calculateTimelineDistribution(data: ContractDetailsAPI | null | undefined): any[] {
    let zeroToThirty = 0;
    let thirtyOneToSixty = 0;
    let sixtyOneToNinety = 0;
    let ninetyOneToOneTwenty = 0;
    let oneTwentyOneToOneEighty = 0;
    let oneEightyOneToThreeSixtyFive = 0;
    let threeSixtySixPlus = 0;

    if (data && data.contracts && Array.isArray(data.contracts)) {
      data.contracts.forEach((contract: any) => {
        if (contract.has_active_arrangement && contract.active_arrangement && contract.active_arrangement.arrangement_date) {
          const creationDate = dayjs(contract.active_arrangement.creation_date);
          const arrangementDate = dayjs(contract.active_arrangement.arrangement_date);
          const daysToExecution = arrangementDate.diff(creationDate, 'day');

          if (daysToExecution <= 30) zeroToThirty++;
          else if (daysToExecution <= 60) thirtyOneToSixty++;
          else if (daysToExecution <= 90) sixtyOneToNinety++;
          else if (daysToExecution <= 120) ninetyOneToOneTwenty++;
          else if (daysToExecution <= 180) oneTwentyOneToOneEighty++;
          else if (daysToExecution <= 365) oneEightyOneToThreeSixtyFive++;
          else threeSixtySixPlus++;
        }
      });
    }

    return [
      { label: '0-30', value: zeroToThirty },
      { label: '31-60', value: thirtyOneToSixty },
      { label: '61-90', value: sixtyOneToNinety },
      { label: '91-120', value: ninetyOneToOneTwenty },
      { label: '121-180', value: oneTwentyOneToOneEighty },
      { label: '180-365', value: oneEightyOneToThreeSixtyFive },
      { label: '365+', value: threeSixtySixPlus }
    ];
  }

  /**
   * Calculates the risk metrics for the scorecard and recommendations based on contract details.
   * 
   * @param data The ContractDetailsAPI response
   * @returns Formatted risk metrics object
   */
  public calculateRiskMetrics(data: ContractDetailsAPI | null | undefined): any {
    let duplicateContracts = 0;
    let duplicateEntries = 0;
    let deletionEntries = 0;
    let totalEntries = 0;
    
    let delinquency90PlusContracts = 0;
    let overdueArrangements = 0;
    let totalActiveArrangements = 0;

    const contracts = data?.contracts || [];
    const uniqueContractsCount = contracts.length;

    contracts.forEach((contract: any) => {
      // Risk / Duplicate calculation
      if (contract.entry_type === 'duplicate') {
        duplicateContracts++;
        duplicateEntries += (contract.entry_count || 0);
      } else if (contract.entry_type === 'deleted') {
        deletionEntries += (contract.entry_count || 0);
      }
      totalEntries += (contract.entry_count || 0);

      if (contract.active_arrangement) {
        totalActiveArrangements++;
        // Overdue check: any contract where days_until < 0
        if (contract.active_arrangement.days_until !== undefined && contract.active_arrangement.days_until !== null && contract.active_arrangement.days_until < 0) {
          overdueArrangements++;
        }
        
        // Delinquency check: if active arrangement is past 90 days overdue
        if (contract.active_arrangement.days_until && contract.active_arrangement.days_until < -90) {
          delinquency90PlusContracts++;
        }
      }
    });

    const duplicateRate = totalEntries > 0 ? (duplicateEntries / totalEntries) * 100 : 0;
    const deletionRate = totalEntries > 0 ? (deletionEntries / totalEntries) * 100 : 0;
    const delinquency90PlusRate = uniqueContractsCount > 0 ? (delinquency90PlusContracts / uniqueContractsCount) * 100 : 0;
    const overdueRate = totalActiveArrangements > 0 ? (overdueArrangements / totalActiveArrangements) : 0; // Value between 0 and 1

    let health = 'Stable';
    if (delinquency90PlusRate > 50 || duplicateRate > 100) health = 'At Risk';
    else if (delinquency90PlusRate < 20 && duplicateRate < 50) health = 'Healthy';

    return {
      duplicateRate,
      deletionRate,
      contractsWithDuplicates: duplicateContracts,
      duplicateEntries,
      deletionEntries,
      delinquency90PlusContracts,
      delinquency90PlusRate,
      overdueArrangements,
      totalActiveArrangements,
      overdueRate,
      portfolioHealth: health,
      uniqueContractsCount
    };
  }

  /**
   * Calculates the distribution of arrangements by value bucket
   * @param data The ContractDetailsAPI response
   * @returns Array of bucket objects for the bar chart
   */
  public calculateSuccessByValue(data: ContractDetailsAPI | null | undefined): any[] {
    const buckets = [
      { min: 0, max: 50, label: '0-50', success: 0, failure: 0 },
      { min: 50, max: 100, label: '50-100', success: 0, failure: 0 },
      { min: 100, max: 150, label: '100-150', success: 0, failure: 0 },
      { min: 150, max: 200, label: '150-200', success: 0, failure: 0 },
      { min: 200, max: 250, label: '200-250', success: 0, failure: 0 },
      { min: 250, max: Infinity, label: '250+', success: 0, failure: 0 }
    ];

    let totalEntries = 0;

    if (data && data.contracts && Array.isArray(data.contracts)) {
      data.contracts.forEach((contract: any) => {
        if (contract.entry_details && Array.isArray(contract.entry_details)) {
          contract.entry_details.forEach((entry: any) => {
            const amount = entry.amount || 0;
            const bucket = buckets.find(b => amount >= b.min && amount < b.max) || buckets[buckets.length - 1]; // Fallback to last bucket
            bucket.success++; 
            totalEntries++;
          });
        }
      });
    }

    return buckets.map(b => ({
      label: b.label,
      success: totalEntries > 0 ? Math.round((b.success / totalEntries) * 100) : 0,
      failure: totalEntries > 0 ? Math.round((b.failure / totalEntries) * 100) : 0
    }));
  }
}
